/* AUTO-SYNCED from PsyKit.io/components/session/PostSessionOverlay.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useState } from "react";
import EmotionPicker from "@/components/session/EmotionPicker";
import ScaleInput from "@/components/session/ScaleInput";
import CheckinComparison from "@/components/session/CheckinComparison";
import type { CheckinLiveData, PostCheckinData } from "@/lib/checkin-types";

interface PostSessionOverlayProps {
  durationSeconds: number;
  passCount: number;
  clientName: string | null;
  audioPreset: string | null;
  onSave: (notes: string, postCheckin: PostCheckinData) => void;
  /** Dismiss the screen WITHOUT ending/saving the session (e.g. opened via the
   *  Post Check-In button for review, or "not yet"). */
  onClose: () => void;
  saving: boolean;
  /** Set when a save attempt failed — shown so the therapist can retry. */
  error?: string | null;
  // ── Pre-session answers, shown read-only for comparison ──
  preGoal: string | null;
  preEmotions: string[];
  preDistress: number | null;
  preCapability: number | null;
  preNote: string | null;
  // ── Post-session check-in (controlled + live-synced by the parent) ──
  emotions: string[];
  value: CheckinLiveData;
  onChange: (next: CheckinLiveData) => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function PostSessionOverlay({
  durationSeconds,
  passCount,
  clientName,
  audioPreset,
  onSave,
  onClose,
  saving,
  error,
  preGoal,
  preEmotions,
  preDistress,
  preCapability,
  preNote,
  emotions,
  value,
  onChange,
}: PostSessionOverlayProps) {
  // Therapist-private session notes (never broadcast to the client).
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    onSave(notes, {
      emotions: value.emotions,
      distress: value.distress,
      capability: value.capability,
      note: value.note.trim() || "",
    });
  };

  const hasPreData =
    preEmotions.length > 0 ||
    preDistress !== null ||
    preCapability !== null ||
    !!(preNote && preNote.trim()) ||
    !!(preGoal && preGoal.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 overflow-y-auto py-6"
      style={{ background: "rgba(8, 12, 20, 0.9)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-4xl flex flex-col gap-5 px-8 py-7 rounded-[4px]"
        style={{
          background: "#0D1B2E",
          border: "1px solid #233D6B",
          borderLeft: "3px solid #C9A84C",
        }}
      >
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p
              className="font-[var(--font-cormorant)] text-2xl font-semibold tracking-wide"
              style={{ color: "#EEF2F7" }}
            >
              Session Complete
            </p>
            <p
              className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.18em] mt-0.5"
              style={{ color: "#3D5470" }}
            >
              Post-session check-in · compare with the start
            </p>
          </div>
          {/* Summary chips */}
          <div className="flex items-center gap-4">
            {clientName && (
              <div className="flex items-center gap-1.5">
                <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>Client</span>
                <span className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.08em]" style={{ color: "#C9A84C" }}>{clientName}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>Duration</span>
              <span className="font-[var(--font-dm-mono)] text-[10px]" style={{ color: "#7A90A8" }}>{formatDuration(durationSeconds)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>Passes</span>
              <span className="font-[var(--font-dm-mono)] text-[10px]" style={{ color: "#7A90A8" }}>{passCount}</span>
            </div>
            {audioPreset && (
              <div className="flex items-center gap-1.5">
                <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>Audio</span>
                <span className="font-[var(--font-dm-mono)] text-[10px] truncate max-w-[80px]" style={{ color: "#7A90A8" }}>{audioPreset}</span>
              </div>
            )}
          </div>
        </div>

        {/* Two columns: pre comparison (left) + editable post check-in (right) */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Pre comparison */}
          <div
            className="rounded-[4px] px-4 py-4"
            style={{ background: "#080C14", border: "1px solid #1A2E4A" }}
          >
            {hasPreData ? (
              <CheckinComparison
                title="Pre-Session"
                goal={preGoal}
                emotions={preEmotions}
                distress={preDistress}
                capability={preCapability}
                note={preNote}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-center px-4 py-8">
                <p className="font-[var(--font-barlow)] text-[11px] leading-relaxed" style={{ color: "#3D5470" }}>
                  No pre-session check-in was recorded for this session.
                </p>
              </div>
            )}
          </div>

          {/* Editable post check-in */}
          <div className="flex flex-col gap-4">
            <p
              className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.2em]"
              style={{ color: "#C9A84C" }}
            >
              Now — Post-Session
            </p>

            <div className="flex flex-col gap-1.5">
              <label className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#7A90A8" }}>
                How are you feeling now? <span style={{ color: "#3D5470" }}>(select all that apply)</span>
              </label>
              <EmotionPicker
                emotions={emotions}
                selected={value.emotions}
                onChange={(v) => onChange({ ...value, emotions: v })}
              />
            </div>

            <ScaleInput
              value={value.distress}
              onChange={(v) => onChange({ ...value, distress: v })}
              label="How much emotional distress does this cause you? (1–10)"
            />
            <ScaleInput
              value={value.capability}
              onChange={(v) => onChange({ ...value, capability: v })}
              label="How capable do you feel to handle it? (1–10)"
            />

            {/* Shared note — live-synced with the client */}
            <div className="flex flex-col gap-1.5">
              <label className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#7A90A8" }}>
                Client note <span style={{ color: "#3D5470" }}>(shared · optional)</span>
              </label>
              <textarea
                value={value.note}
                onChange={(e) => onChange({ ...value, note: e.target.value })}
                placeholder="How the client describes feeling now…"
                rows={2}
                maxLength={1000}
                className="w-full rounded-[4px] font-[var(--font-barlow)] text-[12px] px-3 py-2 outline-none resize-none leading-relaxed"
                style={{ background: "#080C14", border: "1px solid #233D6B", color: "#C8D4E0" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "#C9A84C44"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "#233D6B"; }}
              />
            </div>

            {/* Therapist-private notes */}
            <div className="flex flex-col gap-1.5">
              <label className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#7A90A8" }}>
                Therapist notes <span style={{ color: "#3D5470" }}>(private · optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observations, follow-up plan…"
                rows={2}
                maxLength={2000}
                className="w-full rounded-[4px] font-[var(--font-barlow)] text-[12px] px-3 py-2 outline-none resize-none leading-relaxed"
                style={{ background: "#080C14", border: "1px solid #233D6B", color: "#C8D4E0" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "#C9A84C44"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "#233D6B"; }}
              />
            </div>
          </div>
        </div>

        {/* Save error — the session is NOT lost; the therapist can retry. */}
        {error && (
          <div
            className="rounded-[4px] px-3 py-2 font-[var(--font-barlow)] text-[11px] leading-relaxed"
            style={{ background: "rgba(200,68,68,0.1)", border: "1px solid #8B2E2E", color: "#E08A8A" }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 rounded-[4px] font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.14em] border transition-colors disabled:opacity-40"
            style={{ borderColor: "#233D6B", color: "#7A90A8", background: "transparent" }}
            onMouseEnter={(e) => {
              if (!saving) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "#3D5470";
                (e.currentTarget as HTMLButtonElement).style.color = "#C8D4E0";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = "#233D6B";
              (e.currentTarget as HTMLButtonElement).style.color = "#7A90A8";
            }}
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-[2] py-2.5 rounded-[4px] font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.14em] font-semibold border transition-all disabled:opacity-40"
            style={{ borderColor: "#C9A84C", color: "#C9A84C", background: "transparent" }}
            onMouseEnter={(e) => {
              if (!saving) {
                (e.currentTarget as HTMLButtonElement).style.background = "#C9A84C";
                (e.currentTarget as HTMLButtonElement).style.color = "#080C14";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.color = "#C9A84C";
            }}
          >
            {saving ? "Saving…" : "Save & End Session"}
          </button>
        </div>
      </div>
    </div>
  );
}
